import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { HeaderIcon } from "./AppHeader";
import "./toolWorkspace.css";

const dialogs = [];
let previousOverflow;
const focusable = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"], [contenteditable="true"]';

export function ToolDialog({ title, subtitle, icon, color, onClose, children, compact = false, busy = false, eyebrow = "SEU ESTÚDIO", ...rest }) {
  const id = useId(), panel = useRef(null), close = useRef(null), closeAction = useRef(onClose), busyRef = useRef(busy);
  closeAction.current = onClose; busyRef.current = busy;
  useEffect(() => {
    const previousFocus = document.activeElement;
    if (!dialogs.length) { previousOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; }
    dialogs.push(id);
    if (!document.querySelector('.tour-layer')) close.current?.focus();
    const keydown = event => {
      if (dialogs[dialogs.length - 1] !== id || document.querySelector('.tour-layer')) return;
      // Confirmation dialogs and editor menus own their keyboard interaction.
      if (document.querySelector('[role="alertdialog"]') || [...document.querySelectorAll('[role="menu"], [role="listbox"]')].some(el => el.getClientRects().length)) return;
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); if (!busyRef.current) closeAction.current(); }
      if (event.key === "Tab") {
        const controls = [...panel.current.querySelectorAll(focusable)].filter(el => el.getClientRects().length && el.getAttribute('type') !== 'hidden');
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !panel.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', keydown);
    return () => {
      window.removeEventListener('keydown', keydown);
      const index = dialogs.indexOf(id); if (index !== -1) dialogs.splice(index, 1);
      if (!dialogs.length) document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [id]);
  return createPortal(<div className={`tool-overlay${compact ? " tool-overlay--compact" : ""}`} style={{ "--tool-accent": color || "var(--pc-accent2, #00d4aa)" }} onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
    <section {...rest} ref={panel} className="tool-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={subtitle ? `${id}-description` : undefined}>
      <header className="tool-dialog-header">
        <span className="tool-dialog-icon"><HeaderIcon name={icon} size={25} /></span>
        <div className="tool-dialog-heading"><span className="tool-eyebrow">{eyebrow}</span><h2 id={`${id}-title`}>{title}</h2>{subtitle && <p id={`${id}-description`}>{subtitle}</p>}</div>
        <button ref={close} type="button" className="tool-icon-button" onClick={onClose} disabled={busy} aria-label={`Fechar ${title}`} title="Fechar"><HeaderIcon name="close" size={20} /></button>
      </header>
      <div className="tool-dialog-body">{children}</div>
    </section>
  </div>, document.body);
}

export function ToolSummary({ items }) {
  return <div className="tool-summary">{items.map(item => <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong>{item.detail && <small>{item.detail}</small>}</div>)}</div>;
}

export function ToolSearch({ value, onChange, placeholder = "Pesquisar", children }) {
  return <div className="tool-toolbar"><div className="tool-search"><HeaderIcon name="busca" size={18} /><input aria-label={placeholder} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />{value && <button type="button" aria-label="Limpar busca" onClick={() => onChange("")}><HeaderIcon name="close" size={16} /></button>}</div>{children}</div>;
}

export function ToolButton({ children, secondary, danger, icon, className = "", ...props }) {
  return <button type="button" {...props} className={`tool-button${secondary ? " tool-button--secondary" : ""}${danger ? " tool-button--danger" : ""} ${className}`}>{icon && <HeaderIcon name={icon} size={17} />}{children}</button>;
}

export function ToolEmpty({ icon, title, children }) {
  return <div className="tool-empty"><HeaderIcon name={icon} size={30} /><strong>{title}</strong>{children && <p>{children}</p>}</div>;
}

export function ToolSection({ title, subtitle, children, ...props }) {
  return <section {...props} className="tool-section"><div className="tool-section-title"><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>{children}</section>;
}

export function ToolFilters({ options, value, onChange }) {
  return <div className="tool-filters" aria-label="Filtros">{options.map(option => { const item = typeof option === 'string' ? { label: option, value: option } : option; return <button type="button" key={item.value} aria-pressed={value === item.value} onClick={() => onChange(item.value)}>{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>; })}</div>;
}
